import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CurrencyFormat from "react-currency-format";
import TripExtraProductPage from "./TripExtraProductPage";
import {
  estimateExtrasFromCart,
  extrasCartSignature,
  extrasLinesSignature,
} from "./tripExtrasHelpers";

const ALL_CHIP = { id: "all", label: "All" };
const YOUR_ITEMS_CHIP = { id: "cart", label: "Your items" };

function flattenProducts(categories) {
  const list = [];
  (categories || []).forEach((cat) => {
    (cat.products || []).forEach((p) => list.push(p));
  });
  return list;
}

const TripExtrasStoreSheet = ({
  open,
  onClose,
  categories = [],
  catalog = null,
  totalCount = 0,
  cart = {},
  onChangeQty,
  quoteLoading = false,
  tripPriceCents = 0,
  confirmedExtrasCents = 0,
  confirmedAllInCents = 0,
  confirmedExtrasLines = [],
}) => {
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState("all");
  const [detailProduct, setDetailProduct] = useState(null);

  const allProducts = useMemo(() => flattenProducts(categories), [categories]);

  const { lines: previewLines, totalCents: previewExtrasCents } = useMemo(
    () => estimateExtrasFromCart(catalog, cart),
    [catalog, cart],
  );

  const cartMatchesQuote =
    extrasCartSignature(cart) === extrasLinesSignature(confirmedExtrasLines);
  const isConfirming = quoteLoading;
  const isPendingApply = !cartMatchesQuote && !quoteLoading;

  const displayExtrasCents = previewExtrasCents;
  const displayAllInCents =
    tripPriceCents > 0
      ? tripPriceCents + previewExtrasCents
      : confirmedAllInCents;
  const displayLines = cartMatchesQuote && !quoteLoading
    ? confirmedExtrasLines
    : previewLines;

  const filtered = useMemo(() => {
    let list = allProducts;
    if (chip === "cart") {
      list = allProducts.filter((p) => (cart[p.productId] || 0) > 0);
    } else if (chip !== "all") {
      list = allProducts.filter(
        (p) => (p.meta?.quoteCategory || "materials") === chip,
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.sku || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [allProducts, cart, chip, search]);

  const chips = useMemo(() => {
    const out = [ALL_CHIP];
    const cartCount = Object.values(cart).filter((n) => n > 0).length;
    if (cartCount > 0) out.push(YOUR_ITEMS_CHIP);
    (categories || []).forEach((c) => {
      if (c?.id) out.push({ id: c.id, label: c.label || c.id });
    });
    return out;
  }, [categories, cart]);

  const cartItemCount = Object.values(cart).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0,
  );

  if (!open || typeof document === "undefined") return null;

  const detailQty = detailProduct ? cart[detailProduct.productId] || 0 : 0;

  return createPortal(
    <div className="lp2-extras-sheet-root" role="presentation">
      <button
        type="button"
        className="lp2-extras-sheet-backdrop"
        aria-label="Close supplies"
        onClick={onClose}
      />
      <div
        className={
          "lp2-extras-sheet" + (quoteLoading ? " is-refreshing" : "")
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="lp2-extras-sheet-title"
      >
        <div className="lp2-extras-sheet-handle" aria-hidden="true" />

        {detailProduct ? (
          <TripExtraProductPage
            product={detailProduct}
            qty={detailQty}
            onBack={() => setDetailProduct(null)}
            onChangeQty={(n) => onChangeQty(detailProduct.productId, n)}
            onAdd={() => {
              if (detailQty <= 0) onChangeQty(detailProduct.productId, 1);
              setDetailProduct(null);
            }}
          />
        ) : (
          <>
            <div className="lp2-extras-sheet-head">
              <h3 id="lp2-extras-sheet-title">Moving supplies</h3>
              <button
                type="button"
                className="lp2-extras-sheet-close"
                aria-label="Close"
                onClick={onClose}
              >
                <i className="mdi mdi-close" />
              </button>
            </div>

            <div className="lp2-extras-sheet-search-wrap">
              <input
                type="search"
                className="lp2-extras-sheet-search"
                placeholder="Search boxes, tape, wrap…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                enterKeyHint="search"
              />
            </div>

            <div className="lp2-extras-chips" role="tablist">
              {chips.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={chip === c.id}
                  className={
                    "lp2-extras-chip" + (chip === c.id ? " is-active" : "")
                  }
                  onClick={() => setChip(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="lp2-extras-list">
              {filtered.length === 0 ? (
                <p className="lp2-extras-empty">
                  {totalCount === 0
                    ? "Moving supplies coming soon."
                    : "No products match your search."}
                </p>
              ) : (
                filtered.map((product) => {
                  const qty = cart[product.productId] || 0;
                  const unitZar =
                    product.salePrice != null && product.salePrice > 0
                      ? product.salePrice
                      : product.price;
                  const soldOut =
                    product.stock != null && product.stock <= 0;
                  return (
                    <div
                      key={product.productId}
                      className={
                        "lp2-extras-list-row" +
                        (qty > 0 ? " is-selected" : "") +
                        (soldOut ? " is-sold-out" : "")
                      }
                    >
                      <button
                        type="button"
                        className="lp2-extras-list-main"
                        onClick={() => setDetailProduct(product)}
                      >
                        {product.image ? (
                          <img src={product.image} alt="" />
                        ) : (
                          <span className="lp2-extras-list-thumb-placeholder">
                            <i className="mdi mdi-package-variant" />
                          </span>
                        )}
                        <span className="lp2-extras-list-text">
                          <span className="lp2-extras-list-name">
                            {product.name}
                          </span>
                          <span className="lp2-extras-list-sub">
                            {soldOut ? "Sold out" : "With your driver"}
                          </span>
                        </span>
                        <span className="lp2-extras-list-price">
                          <CurrencyFormat
                            value={unitZar}
                            displayType="text"
                            thousandSeparator
                            prefix="R"
                            decimalScale={0}
                          />
                        </span>
                      </button>
                      <div className="lp2-extras-list-action">
                        {qty > 0 ? (
                          <div className="lp2-extras-stepper lp2-extras-stepper--row">
                            <button
                              type="button"
                              className="lp2-extras-stepper-btn"
                              aria-label={`Decrease ${product.name}`}
                              onClick={() =>
                                onChangeQty(product.productId, qty - 1)
                              }
                            >
                              −
                            </button>
                            <span className="lp2-extras-stepper-qty">{qty}</span>
                            <button
                              type="button"
                              className="lp2-extras-stepper-btn"
                              aria-label={`Increase ${product.name}`}
                              disabled={
                                soldOut ||
                                qty >= (product.meta?.maxQty || 10)
                              }
                              onClick={() =>
                                onChangeQty(product.productId, qty + 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="lp2-extras-add-btn"
                            disabled={soldOut}
                            aria-label={`Add ${product.name}`}
                            onClick={() => onChangeQty(product.productId, 1)}
                          >
                            +
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <div className="lp2-extras-sheet-footer">
          <div
            className="lp2-extras-sheet-footer-body"
            aria-live="polite"
            aria-atomic="true"
          >
            {displayLines.length > 0 ? (
              <ul className="lp2-extras-sheet-lines">
                {displayLines.slice(0, 4).map((line) => (
                  <li key={line.productId || line.name}>
                    <span>
                      {line.qty}× {line.name}
                    </span>
                    <CurrencyFormat
                      value={(line.lineTotalCents ?? (line.unitPriceCents || 0) * (line.qty || 0)) / 100}
                      displayType="text"
                      thousandSeparator
                      prefix="R"
                      decimalScale={2}
                    />
                  </li>
                ))}
                {displayLines.length > 4 ? (
                  <li className="lp2-extras-sheet-lines-more">
                    +{displayLines.length - 4} more item
                    {displayLines.length - 4 === 1 ? "" : "s"}
                  </li>
                ) : null}
              </ul>
            ) : null}

            {tripPriceCents > 0 ? (
              <div className="lp2-extras-sheet-totals">
                <div className="lp2-extras-sheet-total-row">
                  <span>Trip hire</span>
                  <CurrencyFormat
                    value={tripPriceCents / 100}
                    displayType="text"
                    thousandSeparator
                    prefix="R"
                    decimalScale={2}
                  />
                </div>
                {displayExtrasCents > 0 ? (
                  <div className="lp2-extras-sheet-total-row lp2-extras-sheet-total-row--supplies">
                    <span>
                      Supplies ({cartItemCount} item
                      {cartItemCount === 1 ? "" : "s"})
                    </span>
                    <span className="lp2-extras-sheet-total-supplies">
                      +{" "}
                      <CurrencyFormat
                        value={displayExtrasCents / 100}
                        displayType="text"
                        thousandSeparator
                        prefix="R"
                        decimalScale={2}
                      />
                    </span>
                  </div>
                ) : null}
                <div className="lp2-extras-sheet-total-row lp2-extras-sheet-total-row--allin">
                  <span>All-in price</span>
                  <span className="lp2-extras-sheet-total-allin">
                    {isConfirming ? (
                      <span
                        className="lp2-spin lp2-extras-sheet-spinner"
                        aria-hidden="true"
                      />
                    ) : null}
                    <CurrencyFormat
                      value={displayAllInCents / 100}
                      displayType="text"
                      thousandSeparator
                      prefix="R"
                      decimalScale={2}
                    />
                  </span>
                </div>
              </div>
            ) : (
              <span>
                {cartItemCount} item{cartItemCount === 1 ? "" : "s"}
                {displayExtrasCents > 0 ? (
                  <>
                    {" · "}
                    <CurrencyFormat
                      value={displayExtrasCents / 100}
                      displayType="text"
                      thousandSeparator
                      prefix="R"
                      decimalScale={2}
                    />
                  </>
                ) : null}
              </span>
            )}

            {isConfirming ? (
              <p className="lp2-extras-sheet-status">
                Updating your all-in price…
              </p>
            ) : isPendingApply ? (
              <p className="lp2-extras-sheet-status">
                Tap Done to update your all-in price
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="lp2-btn lp2-btn--primary lp2-extras-sheet-done"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TripExtrasStoreSheet;
